import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import {
  Field, reduxForm, formValueSelector, change,
  getFormSyncErrors, submit as submitForm, getFormValues
} from 'redux-form';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';
import Button from 'react-bootstrap/lib/Button';

import Loader from '../../Loader';
import InputFieldComponent from '../../Common/FormField/InputFieldComponent';
import SelectFieldComponent from '../../Common/FormField/SelectFieldComponent';

import validateStripe from './validateStripe';
import submit from './submit';
import generateStripePayoutToken from '../../../helpers/generateStripePayoutToken';
import showToaster from '../../../helpers/showToaster';
import messages from '../../../locale/messages';
import { startPayoutLoading, stopPayoutLoading } from '../../../actions/Payout/addPayoutAction';
import accountFieldSettings from '../../../helpers/accountFieldSettings';

import bt from '../../../components/commonStyle.css';
import s from '../Payout.css';

let Stripe = (props) => {
  const {
    handleSubmit, pristine, submitting, error, stripe, formValues, payoutLoading, businessType, payoutCountry, payoutCurrency,
    startPayoutLoading, change, stopPayoutLoading, intl: { formatMessage }
  } = props;

  const handleSubmitAction = async (event) => {
    event.preventDefault();

    if (formValues && stripe) {
      startPayoutLoading();
      const generateTokens = await generateStripePayoutToken(stripe, formValues);

      if (generateTokens?.status === 200) {
        const accountToken = generateTokens.result?.accountToken;
        const personToken = generateTokens.result?.personToken || null;

        change('PayoutForm', 'isTokenGenerated', true);
        change('PayoutForm', 'accountToken', accountToken);
        change('PayoutForm', 'personToken', personToken);
        await handleSubmit();
      } else {
        showToaster({
          messageId: 'commonError',
          toasterType: 'error',
          requestContent: generateTokens.errorMessage
        });
        stopPayoutLoading();
      }
    }
  };

  const countryField = accountFieldSettings.find(item => item.country === payoutCountry);

  const payoutValue = [
    { value: 'individual', label: formatMessage(messages.payoutIndividual) },
    { value: 'company', label: formatMessage(messages.payoutCompany) },
  ];

  return (
    <div className={cx('inputFocusColor', 'commonListingBg', 'payoutMethodList', 'noMarginBottom')}>
      <form onSubmit={handleSubmit}>
        <h3 className={bt.listingTitleText}>{formatMessage(messages.addPayout)}</h3>

        <Field
          name="businessType"
          component={SelectFieldComponent}
          labelClass={cx(s.labelText)}
          label={formatMessage(messages.payoutType)}
          inputClass={cx(bt.commonControlSelect, 'commonControlSelectRtl')}
          options={payoutValue}
        />
        <div className={businessType === 'individual' ? s.displayGrid : s.displayBlock}>
          <Field
            name="firstname"
            component={InputFieldComponent}
            inputClass={cx(bt.commonControlInput)}
            label={businessType === 'individual' ? formatMessage(messages.payoutFirstName) : formatMessage(messages.payoutCompanyName)}
            placeholder={businessType === 'individual' ? formatMessage(messages.payoutFirstName) : formatMessage(messages.payoutCompanyName)}
          />
          {
            businessType === 'individual' && (
              <Field
                name="lastname"
                component={InputFieldComponent}
                inputClass={cx(bt.commonControlInput)}
                label={formatMessage(messages.payoutLastName)}
                placeholder={formatMessage(messages.payoutLastName)}
              />
            )
          }
        </div>
        {
          countryField?.fields?.map((item) => (
            <Field
              key={item.fieldName}
              name={item.fieldName}
              component={InputFieldComponent}
              inputClass={cx(bt.commonControlInput)}
              label={formatMessage(messages[item.fieldLabel])}
              placeholder={`eg: ${item.placeholder}`}
            />
          ))
        }
        {!countryField && payoutCurrency !== 'EUR' && (
          <Field
            name="routingNumber"
            component={InputFieldComponent}
            inputClass={cx(bt.commonControlInput)}
            label={formatMessage(messages.payoutRouting)}
            placeholder="eg: 110000000"
          />
        )}
        {!countryField && (
          <>
            <Field
              name="accountNumber"
              component={InputFieldComponent}
              inputClass={cx(bt.commonControlInput)}
              label={formatMessage(messages[payoutCurrency !== 'EUR' ? 'accountNumber' : 'ibanNumber'])}
              placeholder="eg: 000123456789"
            />
            <Field
              name="confirmAccountNumber"
              component={InputFieldComponent}
              inputClass={cx(bt.commonControlInput)}
              label={formatMessage(messages[payoutCurrency !== 'EUR' ? 'accountNumber' : 'confirmIbanNumber'])}
              placeholder="eg: 000123456789"
            />
          </>
        )}
        {
          payoutCountry === 'US' && businessType === 'individual' && (
            <Field
              name="ssn4Digits"
              component={InputFieldComponent}
              inputClass={cx(bt.commonControlInput)}
              label={formatMessage(messages.ssn4Digits)}
              placeholder="1234"
            />
          )
        }
        <p className={cx(s.infoBox, bt.space2)}>{formatMessage(messages.payoutStripeInfo)}</p>
        <p className={cx(s.infoBox, bt.space4)}>
          {formatMessage(messages.stripeTokenInfo1)}{' '}
          <a href="https://stripe.com/connect-account/legal" target="_blank" rel="noopener noreferrer" className={s.stripeLink}>
            {formatMessage(messages.stripeTokenInfo2)}
          </a>.
        </p>
        <div className='doubleBtnFlexEnd'>
          <Button
            className={cx(bt.btnLarge, bt.btnPrimaryBorder)}
            onClick={props.previousPage}
          >
            <FormattedMessage {...messages.back} />
          </Button>
          <Loader
            type="button"
            buttonType="button"
            className={cx(bt.btnPrimary, bt.btnLarge, bt.loaderFlex)}
            disabled={pristine || submitting || error || payoutLoading}
            show={payoutLoading}
            label={formatMessage(messages.finish)}
            handleClick={handleSubmitAction}
          />
        </div>
      </form>
    </div>
  );
};

Stripe = reduxForm({
  form: 'PayoutForm',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  validate: validateStripe,
  onSubmit: submit,
})(Stripe);

const selector = formValueSelector('PayoutForm');

const mapState = (state) => ({
  siteName: state.siteSettings.data.siteName,
  availableCurrencies: state?.currency?.availableCurrencies?.results,
  base: state.currency.base,
  payoutLoading: state.reservation.payoutLoading,
  businessType: selector(state, 'businessType'),
  payoutCountry: selector(state, 'country'),
  formValues: getFormValues('PayoutForm')(state),
  errors: getFormSyncErrors('PayoutForm')(state),
  userId: state.account.data.userId
});

const mapDispatch = {
  submitForm,
  change,
  startPayoutLoading,
  stopPayoutLoading
};

export default injectIntl(withStyles(bt, s)(connect(mapState, mapDispatch)(Stripe)));